import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

export interface AuthUser {
  id: number;
  name: string | null;
  surname: string | null;
  phone: string | null;
  mail: string | null;
  department_id: number | null;
  department_name?: string | null;
  level: number | null;
  level_name?: string | null;
  lang: string | null;
  dark_theme: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<boolean>;
  loginSuccess: () => Promise<boolean>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

let bootstrapMePromise: Promise<AuthUser | null> | null = null;
let bootstrapMeLoaded = false;
let bootstrapMeUser: AuthUser | null = null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearSessionCookie = async (): Promise<void> => {
    try {
      await fetch(`${API_BASE}/login/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // no-op
    }
  };

  const fetchMe = async (): Promise<{ user: AuthUser | null; status: number; text?: string }> => {
    const res = await fetch(`${API_BASE}/api/profiles/me`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      return { user: data as AuthUser, status: res.status };
    }
    if (res.status === 401) {
      return { user: null, status: 401 };
    }
    const text = await res.text();
    return { user: null, status: res.status, text };
  };

  const loadMe = async (): Promise<boolean> => {
    try {
      setError(null);

      const result = await fetchMe();

      if (result.user) {
        bootstrapMeLoaded = true;
        bootstrapMeUser = result.user;
        setUser(result.user);
        return true;
      }

      if (result.status === 401) {
        bootstrapMeLoaded = true;
        bootstrapMeUser = null;
        await clearSessionCookie();
        setUser(null);
        return false;
      }

      console.error('[AuthContext] /api/profiles/me failed:', result.status, result.text || '');
      bootstrapMeLoaded = false;
      bootstrapMeUser = null;
      setError(`Profile fetch failed: ${result.status}`);
      setUser(null);
      return false;
    } catch (e: any) {
      console.error('[AuthContext] loadMe error:', e);
      bootstrapMeLoaded = false;
      bootstrapMeUser = null;
      setError(e.message || 'Auth error');
      setUser(null);
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        if (bootstrapMeLoaded) {
          setError(null);
          setUser(bootstrapMeUser);
          return;
        }

        if (!bootstrapMePromise) {
          bootstrapMePromise = fetchMe()
            .then(async (result) => {
              if (result.status !== 401 && result.status !== 200) {
                throw new Error(`Profile fetch failed: ${result.status}`);
              }
              if (result.status === 401) {
                await clearSessionCookie();
              }
              bootstrapMeLoaded = true;
              bootstrapMeUser = result.user;
              return result.user;
            })
            .finally(() => {
              bootstrapMePromise = null;
            });
        }

        const initialUser = await bootstrapMePromise;
        if (!isMounted) return;
        setError(null);
        setUser(initialUser);
      } catch (e: any) {
        if (!isMounted) return;
        console.error('[AuthContext] initial loadMe error:', e);
        setError(e?.message || 'Auth error');
        setUser(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const loginSuccess = async (): Promise<boolean> => {
    return await loadMe();
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/login/logout`, { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error('[AuthContext] logout error:', e);
    } finally {
      bootstrapMeLoaded = true;
      bootstrapMeUser = null;
      setUser(null);
      setError(null);
    }
  };

  const value = useMemo(
    () => ({ user, loading, error, refresh: loadMe, loginSuccess, logout }),
    [user, loading, error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
